import React, { useEffect } from 'react';
import {
  grabSectionByID,
  grabDropdownOptionsByID,
  isFormReviewedOrDelivered,
} from './helpers';
import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import { DropdownItem } from './formData.types';
import { useIntl } from 'react-intl';
import {
  getMetrics,
  mapVertical,
} from '../../PerformancePortal/CoachingForm/ContactReview/Data';
import { ccxOldMetrics } from '../../PerformancePortal/CoachingForm/CoachingDetails/Data';
import { ConsolidatedContact } from '../../api/CoachingNotes/schema';
import { Profile } from '../../Types';

interface RenderDropdownsFromDataProps {
  profile?: Profile;
  data?: any;
  dropdownSectionID: string;
  setFormState: (...args: any) => void;
  styles: any;
  formState: {
    contactInfo?: ConsolidatedContact;
    dropdownItems?: DropdownItem[] | undefined;
    // allow anything else
    [key: string]: any;
  };
  handleSelectDropdownValue: (t: string) => (selected: string) => void;
  formPrefix: string;
}

const rapMetrics = [
  'HMD',
  'HMD_YES',
  'HMD_NO',
  'HMD_OFFER_RATE',
  'RAP_REPEAT',
  'RAP',
  'PRR',
];

const RenderDropdownsFromData: React.FC<
  RenderDropdownsFromDataProps
> = props => {
  const {
    profile,
    data,
    dropdownSectionID,
    setFormState,
    styles,
    formState,
    handleSelectDropdownValue,
    formPrefix,
  } = props;

  const intl = useIntl();

  const isReviewedOrDelivered = isFormReviewedOrDelivered(
    formState?.contactInfo?.contactID,
    formState?.contactInfo?.contactStatus
  );

  // TODO: It appears that we are using this css to avoid rendering the vertical dropdown option in all forms. This should be done in code/logic, not via css.
  const itemsToRender =
    formState?.dropdownItems?.filter(
      (d: DropdownItem) => d?.options?.length > 0
    ) ?? [];

  // @TODO - Remove old CCX metrics and hiding logic  - SIM: https://sim.amazon.com/issues/CSKoala-1229
  // Hiding old CCX Metrics from the option list for backward compatible solution
  const verticalDropdown = formState.dropdownItems!.find(
    item => item.type === 'vertical'
  );
  const selectedVertical = verticalDropdown?.selected;
  const mappedVertical = mapVertical(selectedVertical, profile);
  const metricPerVertical = getMetrics(mappedVertical).map(metric =>
    metric.value.toString()
  );

  const ccxOldMetricDisplay = (selectedMetric: string | undefined) => {
    if (selectedMetric && ccxOldMetrics.includes(selectedMetric)) {
      return (
        <SelectOption
          value={selectedMetric}
          label={intl.formatMessage({
            id: selectedMetric,
            defaultMessage: selectedMetric,
          })}
        />
      );
    }
    return <></>;
  };

  const rapOldMetricDisplay = (selectedMetric: string | undefined) => {
    if (selectedMetric && rapMetrics.includes(selectedMetric)) {
      return (
        <SelectOption
          value={selectedMetric}
          label={intl.formatMessage({
            id: selectedMetric,
            defaultMessage: selectedMetric,
          })}
        />
      );
    }
    return <></>;
  };

  return (
    <Box className={styles.dropdownItemContainer}>
      {itemsToRender.map((d: DropdownItem, index: number) => (
        <div className={styles[`baseDropdownItem-${index}`]}>
          <Select
            disabled={isReviewedOrDelivered}
            label={intl.formatMessage({
              id: d.placeholder.TRANSLATION_ID,
              defaultMessage: d.placeholder.DEFAULT_MESSAGE,
            })}
            value={d.selected}
            onChange={handleSelectDropdownValue(d.type)}
            error={false}
            size='xlarge'
          >
            {d.type === 'metric'
              ? d.options
                  .filter(option =>
                    metricPerVertical.includes(option.DEFAULT_MESSAGE)
                  )
                  .map(option => (
                    <SelectOption
                      value={option.DEFAULT_MESSAGE}
                      label={intl.formatMessage({
                        id: option.TRANSLATION_ID ?? '',
                        defaultMessage: option.DEFAULT_MESSAGE ?? '',
                      })}
                    />
                  ))
                  .concat(ccxOldMetricDisplay(d.selected))
                  .concat(rapOldMetricDisplay(d.selected))
              : d.options.map(option =>
                  option.DEFAULT_MESSAGE ? (
                    <SelectOption
                      value={option.DEFAULT_MESSAGE}
                      label={intl.formatMessage({
                        id: option.TRANSLATION_ID ?? '',
                        defaultMessage: option.DEFAULT_MESSAGE ?? '',
                      })}
                    />
                  ) : (
                    <></>
                  )
                )}
          </Select>
        </div>
      ))}
    </Box>
  );
};

export default RenderDropdownsFromData;
